import { evaluate } from '../lib/flip';
import { percent, pounds } from '../lib/format';
import { computeStats } from '../lib/stats';
import { useStore } from '../state/store';
import { go } from '../state/useHashRoute';

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'pos' | 'neg' }) {
  return (
    <div className="stat">
      <span className="label">{label}</span>
      <span className={`big ${tone ?? ''}`}>{value}</span>
      {note && <small className="muted">{note}</small>}
    </div>
  );
}

/** Bars for the profit on each sold car. Plain SVG so it needs no chart library. */
function ProfitBars({ items }: { items: { title: string; profit: number }[] }) {
  if (items.length === 0) return null;
  const max = Math.max(1, ...items.map((i) => Math.abs(i.profit)));
  const w = 520;
  const rowH = 30;
  const mid = w / 2;
  return (
    <svg role="img" aria-label="Profit for each sold car" viewBox={`0 0 ${w} ${items.length * rowH + 8}`} className="bars">
      <line x1={mid} x2={mid} y1={0} y2={items.length * rowH + 8} className="axis" />
      {items.map((it, i) => {
        const len = (Math.abs(it.profit) / max) * (mid - 150);
        return (
          <g key={`${it.title}-${i}`} transform={`translate(0 ${i * rowH + 4})`}>
            <rect x={it.profit >= 0 ? mid : mid - len} width={len} height={rowH - 10} className={it.profit >= 0 ? 'bar pos' : 'bar neg'} rx={3} />
            <text x={it.profit >= 0 ? mid + len + 6 : mid - len - 6} y={(rowH - 10) / 2 + 4} textAnchor={it.profit >= 0 ? 'start' : 'end'} className="bar-label">{pounds(it.profit)}</text>
            <text x={4} y={(rowH - 10) / 2 + 4} className="bar-title">{it.title.length > 24 ? `${it.title.slice(0, 23)}…` : it.title}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function DashboardPage() {
  const { data, addDemoCars } = useStore();
  const s = computeStats(data.cars);
  const soldItems = data.cars.filter((c) => c.status === 'sold').map((c) => ({ title: c.title, profit: evaluate(c.deal).profit }));
  const cash = data.settings.startingCash;

  return (
    <div className="page">
      <h1>Dashboard</h1>
      {data.cars.length === 0 ? (
        <div className="card empty">
          <h2>Nothing to show yet</h2>
          <p>Your totals appear here once you have saved cars. Start with the calculator, or load example cars.</p>
          <div className="actions">
            <button type="button" className="btn primary" onClick={() => go('/calculator')}>Open the calculator</button>
            <button type="button" className="btn" onClick={addDemoCars}>Load example cars</button>
          </div>
        </div>
      ) : (
        <>
          <section className="stats" aria-label="Totals">
            <Stat label="Profit made" value={pounds(s.realisedProfit)} tone={s.realisedProfit > 0 ? 'pos' : s.realisedProfit < 0 ? 'neg' : undefined} note={`${s.soldCount} car${s.soldCount === 1 ? '' : 's'} sold`} />
            <Stat label="Average per car" value={s.soldCount ? pounds(s.averageProfit) : '–'} note={s.soldCount ? `${percent(s.winRatePct)} made money` : 'No sales yet'} />
            <Stat label="Average days to sell" value={s.averageDays === null ? '–' : s.averageDays.toFixed(0)} note="Buying to selling" />
            <Stat label="Cash tied up" value={pounds(s.cashTiedUp)} note={`${s.openCount} car${s.openCount === 1 ? '' : 's'} not sold yet`} />
            <Stat label="Expected from those" value={pounds(s.openExpectedProfit)} note="If they sell as entered" />
            <Stat label="Cash set aside" value={pounds(cash)} note={cash > 0 ? `${pounds(Math.max(0, cash - s.cashTiedUp))} not in cars` : 'Set this in Settings'} />
          </section>

          {s.soldCount > 0 && (
            <section className="card" aria-labelledby="sold-h">
              <h2 id="sold-h">Profit on each sold car</h2>
              <ProfitBars items={soldItems} />
              <p className={s.versusPlan < 0 ? 'neg' : 'pos'}>
                Against your plans, sold cars came out {pounds(Math.abs(s.versusPlan))} {s.versusPlan < 0 ? 'worse' : 'better'}.
                {s.versusPlan < 0 ? ' Use this to make your next estimates more cautious.' : ''}
              </p>
              {s.best && s.worst && s.best.title !== s.worst.title && (
                <p className="muted">Best: {s.best.title} ({pounds(s.best.profit)}). Worst: {s.worst.title} ({pounds(s.worst.profit)}).</p>
              )}
            </section>
          )}
          {s.cashTiedUp > cash && cash > 0 && (
            <p className="warn" role="note">More cash is tied up in cars than the {pounds(cash)} you set aside. Check your cash figure in Settings.</p>
          )}
        </>
      )}
      <p className="muted small">
        If you claim Universal Credit or other benefits, tell them about trading income. Regular buying and selling for profit counts as a business.
        See <a href="#/rules">Know the rules</a>.
      </p>
    </div>
  );
}
